import { useEffect, useMemo, useState } from "react";
import { toSecondaryQuantity } from "@heartinz/finance";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import { usePermission } from "../../../context/PermissionContext";
import { parseAmount } from "../../../templates/modules/invoiceUtils";
export const generateInvPurchaseInvoicePdf = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme, 
  storeSettings,
) => {
  const { documentConfig,hasPayment } = invoiceConfig;
  const doc = new jsPDF("p", "mm", "a4");

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const margin = 5;
  const contentWidth = pageWidth - margin * 2;
  let currentY = margin;
  // --- 1. HEADER CONTAINER BAR ---
  const themeColor = selectedTheme?.templateTheme?.primaryColor || "#F0F5FF";
  doc.setFillColor(themeColor);
  doc.roundedRect(margin, currentY, contentWidth, 36, 2, 2, "F");


  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);

  if (
    (documentConfig?.supplierInfo?.name || documentConfig?.supplierInfo?.number)
  ) {
    doc.setFont("Inter-Bold", "bold");
    doc.text("Supplier Details:", margin + 6, currentY + 20);    
    doc.setFontSize(9);
  
    doc.text(documentConfig?.supplierInfo?.name || "", margin + 6, currentY + 25);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Number : ${documentConfig?.supplierInfo?.number || ""}`,
      margin + 6,
      currentY + 30,
    );
  }

  // Right Side: Document Title
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(20);
  doc.setTextColor(20, 40, 100);
  doc.text(documentConfig?.title, pageWidth - margin - 6, currentY + 11, {
    align: "right",
  });

  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);

  const purchaseNoStr = documentConfig?.number || "-";
  const dateStr = documentConfig?.Date?.date;

  // --- EXACT RIGHT METADATA ALIGNMENT STRATEGY ---
  const rightAlignX = pageWidth - margin - 6;

  // 1. Position Purchase No Row cleanly
  const labelNo = documentConfig?.numberLabel;
  doc.setFont("Inter-Regular", "normal");
  const valueNoWidth = doc.getTextWidth(purchaseNoStr);

  // Draw the bold label exactly before the value string starts
  doc.setFont("Inter-Bold", "bold");
  doc.text(labelNo, rightAlignX - valueNoWidth, currentY + 22, {
    align: "right",
  });
  doc.setFont("Inter-Regular", "normal");
  doc.text(purchaseNoStr, rightAlignX, currentY + 22, { align: "right" });

  // 2. Position Date Row cleanly
  const labelDate = "Date: ";
  doc.setFont("Inter-Regular", "normal");
  const valueDateWidth = doc.getTextWidth(dateStr);

  // Draw the bold label exactly before the date string starts
  doc.setFont("Inter-Bold", "bold");
  doc.text(labelDate, rightAlignX - valueDateWidth, currentY + 27, {
    align: "right",
  });
  doc.setFont("Inter-Regular", "normal");
  doc.text(dateStr, rightAlignX, currentY + 27, { align: "right" });

  currentY += 40;
  // --- 2. PURCHASE ITEMS TABLE ---
  if (documentConfig?.purchaseItems?.length > 0) {
    const itemRows = documentConfig?.purchaseItems.map((item, index) => {
      const rateStr = `${item.purchasePriceWithTax || 0} / ${item.unitConfig?.primaryUnitCode || "Unit"}`;
      const qtyStr = item?.isSecondaryUnit
        ? `${toSecondaryQuantity(item.purchaseQuantity || 0, item?.unitConfig || {})?.secondaryQty} ${item?.unitConfig?.secondaryUnitCode || ""}`
        : `${item.purchaseQuantity || 0} ${item?.unitConfig?.primaryUnitCode || ""}`;

      return [
        index + 1,
        item.code || "-",
        item.hsnsac || "-",
        item.title || "",
        `${item.purchaseMrp}`,

        rateStr,
        qtyStr,
        `${item.purchaseTaxPercentage}%`,
        `${item.purchaseTaxAmount}`,
        `${item.purchaseDiscount}`,
        `${item.lineTotalPurchasePriceWithoutTax}`,
        `${item.lineTotalPurchasePriceWithTax}`,
      ];
    });

    autoTable(doc, {
      startY: currentY,
      margin: { left: margin, right: margin },
      head: [
        [
          "#",
          "Barcode",
          "HSN",
          "Description",
          "MRP",
          "Rate",
          "Qty",
          "Tax %",
          "Tax Amt",
          "Discount",
          "Base Price",
          "Total",
        ],
      ],
      body: itemRows,
      theme: "plain",
      headStyles: {
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme?.templateTheme?.foregroundColor,
        fontStyle: "bold",
        font: "Inter-Bold",
        halign: "center",
        valign: "middle",
      },

      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        fontSize: 6.5,
        halign: "middle",
        cellPadding: 1,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        textColor: "#041121",
      },
      columnStyles: {
        0: { cellWidth: 8, halign: "center", valign: "middle" },
         1: { halign: "center", valign: "middle" },
        2: { cellWidth: 10, halign: "center", valign: "middle" },
        3: {  cellWidth: "auto", halign: "left", valign: "middle" },
        4: {halign: "right", valign: "middle" },
        5: { halign: "right", valign: "middle" },
        6: {cellWidth: 12, halign: "center", valign: "middle" },
        7: { cellWidth: 12, halign: "center", valign: "middle" },
        8: { halign: "right", valign: "middle" },
        9: { halign: "right", valign: "middle" },
        10: { halign: "right", valign: "middle" },
        11: { halign: "right", valign: "middle" },
      },
    });

    currentY = doc.lastAutoTable.finalY + 4;
  }

  // --- 3. SERVICES SECTION ---
  if (documentConfig?.purchaseServices?.length > 0) {
    if (currentY + 20 > pageH - 20) {
      doc.addPage();
      currentY = margin;
    }
    currentY+=2
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.setTextColor(20, 40, 100);
    doc.text("SERVICE", pageWidth / 2, currentY, { align: "center" });
    currentY += 4;

    const serviceRows = documentConfig?.purchaseServices.map((item, index) => [
      index + 1,
      item.hsnsac || "-",
      item.title || "",
      `${item.purchaseTaxPercentage}%`,
      `${item.lineTotalPurchasePriceWithoutTax}`,
      `${item.lineTotalPurchasePriceWithTax}`,
    ]);

    autoTable(doc, {
      startY: currentY,
      margin: { left: margin, right: margin },
      head: [["#", "HSN", "Description", "Tax %", "Base Price", "Total"]],
      body: serviceRows,
      theme: "plain",
      headStyles: {
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme?.templateTheme?.foregroundColor,
        fontStyle: "bold",
        font: "Inter-Bold",
        halign: "center",
        valign: "middle",
      },

      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        fontSize: 7,
        halign: "middle",
        cellPadding: 1,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        textColor: "#041121",
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { halign: "center" },
        2: { cellWidth: "auto", halign: "left" },
        3: { halign: "center" },
        4: { halign: "right",cellWidth:25 },
        5: { halign: "right", cellWidth:25 },
      },
      didParseCell: function (data) {
        if (data.section === "head") {
          const colId = data.column.index;
         if ([0,1,2, 3, 4, 5].includes(colId))
          data.cell.styles.halign = "center";
        }
      },
    });

    currentY = doc.lastAutoTable.finalY + 4;
  }

  // --- 4. SUMMARY & BREAKDOWN SECTION ---
  // --- 4. SUMMARY & BREAKDOWN SECTION ---
  // 1. Prepare dynamic totals row data
  const totalsRows = [
    ["Total", `${documentConfig?.totalPurchasePriceWithoutTax}`],
    ["GST", `${documentConfig?.totalPurchaseTaxAmount}`],
  ];

  if (documentConfig?.sgstAmount > 0) {
    totalsRows.push(["SGST", `${documentConfig.sgstAmount}`]);
  }
  if (documentConfig?.cgstAmount > 0) {
    totalsRows.push(["CGST", `${documentConfig?.cgstAmount}`]);
  }
  if (documentConfig?.igstAmount > 0) {
    totalsRows.push(["IGST", `${documentConfig?.igstAmount}`]);
  }

  totalsRows.push(["Grand Total", `${documentConfig?.totalAmount}`]);

  // 2. Safely check page overflow before rendering components
  const estimatedHeight = totalsRows.length * 7; // Approx height based on rows
  if (currentY + estimatedHeight > pageH - 20) {
    doc.addPage();
    currentY = margin;
  }

  const splitWidth = contentWidth - 70;
  const rightX = margin + splitWidth;

  // 3. Render the Right Side Financial Summary Breakdown Table first
  autoTable(doc, {
    startY: currentY,
    margin: { left: rightX, right: margin },
    body: totalsRows,
    theme: "plain",
    styles: {
      fontSize: 8.5,
      font: "Inter-Regular",
      cellPadding: 2,
      textColor: "#041121",
      lineColor: selectedTheme?.templateTheme?.borderColor || [220, 224, 230],
      lineWidth: 0.2,
    },
    columnStyles: {
      0: { cellWidth: 32, halign: "left" },
      1: { cellWidth: 38, halign: "right" },
    },
    didParseCell: function (cellData) {
      if (cellData.row.index === totalsRows.length - 1) {
        cellData.cell.styles.fontStyle = "bold";
        cellData.cell.styles.font = "Inter-Bold";
        cellData.cell.styles.fontSize = 9.5;
        cellData.cell.styles.textColor = selectedTheme?.templateTheme
          ?.foregroundColor || [20, 40, 100];
        cellData.cell.styles.fillColor = themeColor || [240, 244, 255];
      }
    },
  });

  const dynamicSummaryHeight = doc.lastAutoTable.finalY - currentY;

  doc.setDrawColor(selectedTheme?.templateTheme?.borderColor || "#DCE0E6");
  doc.setFillColor(252, 253, 254);
  doc.rect(margin, currentY, splitWidth, dynamicSummaryHeight, "FD");
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(0, 0, 0);
  doc.text("Amount In Words", margin + 2, currentY + 6);

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);

  const wordsText = documentConfig?.amountInWords || "Zero Rupees Only";
  const wrappedWords = doc.splitTextToSize(wordsText, splitWidth - 10);
  doc.text(wrappedWords, margin + 2, currentY + 12);
  currentY = doc.lastAutoTable.finalY + 4;
 const paymentModes = [
    { label: "Cash", amount: documentConfig?.cashPaid },
    { label: "Card", amount: documentConfig?.cardPaid },
    { label: "Online Amount", amount: documentConfig?.onlinePaid },
    { label: "UPI", amount: documentConfig?.upiPaid },
    { label: "Balance Due", amount: documentConfig?.balanceDue },
  ];

  const validPayments = paymentModes.filter(
    (item) => parseAmount(item.amount) > 0,
  );

  if (hasPayment && validPayments.length > 0) {
    const paymentRows = validPayments.map((p) => [p.label, `${p.amount}`]);
    const estimatedPaymentHeight = paymentRows.length * 7 + 10;
   
    if (currentY + estimatedPaymentHeight > pageH - 20) {
      doc.addPage();
      currentY = margin;
    }

    autoTable(doc, {
      startY: currentY,
      margin: { left: margin },
      tableWidth: contentWidth * 0.38,
      head: [
        [
          {
            content: "Payment Mode",
            colSpan: 2,
            styles: { halign: "center" },
          },
        ],
      ],
      body: paymentRows,
      theme: "plain",
      headStyles: {
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme?.templateTheme?.foregroundColor,
        fontStyle: "bold",
        font: "Inter-Bold",
        fontSize: 9,
      },
      styles: {
        font: "Inter-Regular",
        fontSize: 8.5,
        cellPadding: 2,
        textColor: "#041121",
        lineColor: selectedTheme?.templateTheme?.borderColor || [220, 224, 230],
        lineWidth: 0.2,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right", fontStyle: "bold", font: "Inter-Bold" },
      },
    });

    currentY = doc.lastAutoTable.finalY + 8;
  }
  if (storeSettings?.negobillBranding) {
    currentY = pageH - 12;

    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    doc.setTextColor(0, 0, 0);

    const normPart = "Powered by ";
    const boldPart = "NegoBill.com";
    const totalW = doc.getTextWidth(normPart) + doc.getTextWidth(boldPart);

    let startX = (pageWidth - totalW) / 2;

    doc.setFont("Inter-Regular", "normal");
    doc.text(normPart, startX, currentY);
    startX += doc.getTextWidth(normPart);

    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(boldPart, startX, currentY);
  }

  // if (typeof setDownload === "function") setDownload(false);
  return doc;
};
const PurchaseView = ({
  data,
  selectedTheme,  
  invoiceConfig,
  onDone,
}) => {
  const { documentConfig ,hasPayment} = invoiceConfig;

  const imageSrc = "";

  const { storeSettings } = usePermission();

  return (
    <>
      <div className="tm_container" id="element-to-print">
        <div className="tm_invoice_wrap">
          <div className="tm_invoice tm_style1 tm_type1">
            <div className="tm_invoice_in">
              <table
                width="100%"
                className="pdfcontent "
                style={{
                  borderRadius: "15px",
                  overflow: "hidden",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <tbody>
                  <tr>
                    <td
                      width="70%"
                      style={{ textAlign: "left", padding: "1rem" }}
                    >
                      {imageSrc && (
                        <div
                          className="tm_logo"
                          style={{
                            position: "relative",
                            width: "100px",
                            height: "auto",
                          }}
                        >
                          <img
                            src={imageSrc}
                            alt="logo"
                            style={{
                              position: "absolute",
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            className="img-rounded logo mb-0"
                          />
                        </div>
                      )}
                    </td>

                    <td
                      width="30%"
                      style={{ textAlign: "center", padding: "1rem" }}
                    >
                      <span
                        className="tm_f27 tm_text_uppercase"
                        style={{
                          color: selectedTheme?.templateTheme?.foregroundColor,
                          fontStyle: "bold",
                        }}
                      >
                        {documentConfig?.title}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td width="70%" className="hederTd">
                      { (documentConfig?.supplierInfo?.name ||
                          documentConfig?.supplierInfo?.number) && (
                          <>
                            <p
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                marginBottom: "0px",
                              }}
                            >
                              {/* {data.customer?.name || ""} */}
                              Supplier Details:
                            </p>
                            <p
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                marginBottom: "0px",
                                color:
                                  selectedTheme?.templateTheme?.foregroundColor,
                              }}
                            >
                              {/* {data.customer?.name || ""} */}
                              {documentConfig?.supplierInfo?.name}
                            </p>
                            <div
                              style={{
                                fontSize: "12px",
                                lineHeight: "18px",
                                marginBottom: "0px",
                              }}
                            >
                              <p className="mb-0">
                                {" "}
                                Number : {documentConfig?.supplierInfo?.number}
                                {/* {data.customer?.phone || ""} */}
                              </p>
                              {/* <p className="mb-0">
                                            eMail : {data?.estimateFromId?.email}
                                            {data.customer?.email || ""}
                                          </p>
                                          {data?.estimateFromId?.cin && (
                                            <p className="mb-0">
                                              Corporate Identity # :{" "}
                                              {data?.estimateFromId?.cin &&
                                                data?.estimateFromId?.cin}
                                              {data.customer?.email || ""}
                                            </p>
                                          )} */}
                            </div>
                          </>
                        )}
                    </td>
                    <td
                      width="30%"
                      className="hederTd"
                      style={{ textAlign: "center" }}
                    >
                      <p className="mb-0">
                        <span style={{ fontWeight: 600 }}>
                          {documentConfig?.numberLabel}
                        </span>{" "}
                        {documentConfig?.number}
                      </p>
                      <p className=" mb-0">
                        <span style={{ fontWeight: 600 }}>Date: </span>{" "}
                        {/* {data.customer?.pannumber || ""} */}
                        {documentConfig?.Date?.date}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
              {documentConfig?.purchaseItems?.length > 0 && (
                <table className="table table-bordered align-middle mt-2"  style={{
    width: "100%",
    tableLayout: "fixed",
    fontSize: "9px",
  }}>
                  <thead className="table-light">
                    <tr className="text-center">
                      <th className="p-1">S.No</th>
                      <th className="p-1">Barcode</th>
                      <th className="p-1">HSN</th>
                      <th className="p-1">Description</th>
                      <th className="p-1">MRP</th>
                      <th className="p-1">Rate</th>
                      <th className="p-1">Qty</th>
                      <th className="p-1">Tax %</th>
                      <th className="p-1">Tax Amt</th>
                      <th className="p-1">Discount</th>
                      <th className="p-1">Base Price</th>
                      <th className="p-1">Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {documentConfig?.purchaseItems?.map((item, index) => (
                      <tr key={index} className="text-center font-9 ">
                        <td className="p-1">{index + 1}</td>
                        <td  className="p-1">{item.code}</td>
                        <td  className="p-1">{item.hsnsac || "-"}</td>

                        <td  className="p-1">
                          <div className="text-left font-9 p-1">{item.title}</div>
                          {/* {item.note && <small>{item.note}</small>} */}
                        </td>

                        <td  className="p-1"><div className="text-right  font-9">{item.purchaseMrp}</div></td>

                        <td  className="p-1"><div className="text-right  font-9">
                          {item.purchasePriceWithTax}{" "}
                          {item.unitConfig?.primaryUnitCode || "Unit"}
                          </div>
                        </td>

                        <td  className="p-1">
                          {" "}
                          {item?.isSecondaryUnit
                            ? `${
                                toSecondaryQuantity(
                                  item.purchaseQuantity || 0,
                                  item?.unitConfig || {},
                                )?.secondaryQty
                              } ${item?.unitConfig?.secondaryUnitCode || ""}`
                            : `${item.purchaseQuantity || 0} ${item?.unitConfig?.primaryUnitCode || ""}`}
                        </td>

                        <td  className="p-1">{item.purchaseTaxPercentage}%</td>

                        <td  className="p-1"><div className="text-right  font-9">{item.purchaseTaxAmount}</div></td>

                        <td  className="p-1"><div className="text-right  font-9">{item.purchaseDiscount}</div></td>

                        <td  className="p-1"><div className="text-right  font-9">{item.lineTotalPurchasePriceWithoutTax}</div></td>

                        <td  className="p-1"><div className="text-right font-9 ">{item.lineTotalPurchasePriceWithTax}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {documentConfig?.purchaseServices?.length > 0 && (
                <>
                  <div
                    style={{
                      textAlign: "center",
                      fontWeight: "600",
                      marginTop: "10px",
                      color: selectedTheme?.templateTheme?.foregroundColor,
                    }}
                  >
                    SERVICE
                  </div>
                  <table className="table table-bordered align-middle mt-2 mb-1 font-9">
                    <thead className="table-light">
                      <tr className="text-center">
                        <th>S.No</th>
                        <th>HSN</th>
                        <th>Description</th>
                        <th>Tax %</th>
                        <th>Base Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {documentConfig?.purchaseServices?.map((item, index) => (
                        <tr key={index} className="text-center">
                          <td  className="p-1">{index + 1}</td>

                          <td  className="p-1">{item.hsnsac || "-"}</td>

                          <td  className="p-1">
                            <div className="text-left font-9">{item.title}</div>
                            {/* {item.description && (
                                            <small>{item.description}</small>
                                          )} */}
                          </td>

                          <td  className="p-1">{item.purchaseTaxPercentage}%</td>

                          <td  className="p-1"> <div className="text-right font-9">{item.lineTotalPurchasePriceWithoutTax}</div></td>

                          <td  className="p-1"><div className="text-right font-9">{item.lineTotalPurchasePriceWithTax}</div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              <div className="row mt-1">
                <div className="col-12">
                  <div
                    className="mt-0 "
                    style={{
                      display: "flex",
                      border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                    }}
                  >
                    {/* Amount in Words */}
                    <div
                      className="p-2"
                      style={{
                        flex: 1,

                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                       
                        display: "flex",
                        alignItems: "top",
                      }}
                    >
                      <div className="font-11">
                        <strong>Amount In Words</strong>
                        <br />
                        {documentConfig?.amountInWords}
                      </div>
                    </div>

                    {/* Totals */}
                    <div style={{ width: "260px" }}>
                      <table
                        className="table table-sm mb-0"
                        style={{ fontSize: "11px" }}
                      >
                        <tbody>
                          <tr>
                            <td>Total</td>
                            <td className="text-end">
                              {documentConfig?.totalPurchasePriceWithoutTax}
                            </td>
                          </tr>

                          <tr>
                            <td>GST</td>
                            <td className="text-end">
                              {documentConfig?.totalPurchaseTaxAmount}
                            </td>
                          </tr>

                          {documentConfig?.sgstAmount > 0 && (
                            <tr>
                              <td>SGST</td>
                              <td className="text-end">
                                {documentConfig?.sgstAmount}
                              </td>
                            </tr>
                          )}

                          {documentConfig?.cgstAmount > 0 && (
                            <tr>
                              <td>CGST</td>
                              <td className="text-end">
                                {documentConfig?.cgstAmount}
                              </td>
                            </tr>
                          )}

                          {documentConfig?.igstAmount > 0 && (
                            <tr>
                              <td>IGST</td>
                              <td className="text-end">
                                {documentConfig?.igstAmount}
                              </td>
                            </tr>
                          )}

                          <tr
                            style={{
                              fontWeight: "700",
                              background: "#f3f3f3",
                              borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              color:
                                selectedTheme?.templateTheme?.foregroundColor,
                            }}
                          >
                            <td>Grand Total</td>
                            <td className="text-end">
                              {documentConfig?.totalAmount}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
             {hasPayment &&  (
                      <table
                        className="items"
                        width="38%"
                        style={{
                          marginTop: "4px",
                          // borderCollapse: "collapse",
                        }}
                        cellPadding="4"
                      >
                        <thead>
                          <tr>
                            <th
                              colSpan={2}
                              className="force-style"
                              style={{
                                width: "100%",
                                textAlign: "center",
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                "--th-bg":
                                  selectedTheme?.templateTheme?.primaryColor,
                                "--th-color":
                                  selectedTheme?.templateTheme?.foregroundColor,
                              }}
                            >
                              Payment Mode
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseAmount(documentConfig?.cashPaid)  > 0&& (
                            <tr>
                              <td
                                className="force-style"
                                style={{
                                  textAlign: "start",
                                  fontSize: "12px",
                                  width: "10%",
                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                }}
                              >
                                Cash
                              </td>
                              <td
                                className="force-style"
                                style={{
                                  textAlign: "start",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  width: "10%",
                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                }}
                              >
                                {documentConfig?.cashPaid}
                              </td>
                            </tr>
                          )}
                          {parseAmount(documentConfig?.cardPaid) > 0 && (
                            <tr>
                              <td
                                className="force-style"
                                style={{
                                  textAlign: "start",
                                  fontSize: "12px",
                                  width: "10%",
                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                }}
                              >
                                Card
                              </td>
                              <td
                                className="force-style"
                                style={{
                                  textAlign: "start",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  width: "10%",
                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                }}
                              >
                                {documentConfig?.cardPaid}
                              </td>
                            </tr>
                          )}
                          {parseAmount(documentConfig?.onlinePaid) > 0 && (
                            <tr>
                              <td
                                className="force-style"
                                style={{
                                  textAlign: "start",
                                  fontSize: "12px",
                                  width: "10%",
                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                }}
                              >
                                Online Amount
                              </td>
                              <td
                                className="force-style"
                                style={{
                                  textAlign: "start",
                                  fontSize: "12px",
                                  width: "10%",
                                  fontWeight: "600",
                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                }}
                              >
                                {documentConfig?.onlinePaid}
                              </td>
                            </tr>
                          )}
                          {parseAmount(documentConfig?.upiPaid) > 0 && (
                            <tr>
                              <td
                                className="force-style"
                                style={{
                                  textAlign: "start",
                                  fontSize: "12px",
                                  width: "10%",
                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                }}
                              >
                                UPI
                              </td>
                              <td
                                className="force-style"
                                style={{
                                  textAlign: "start",
                                  fontSize: "12px",
                                  width: "10%",
                                  fontWeight: "600",
                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                }}
                              >
                                {documentConfig?.upiPaid}
                              </td>
                            </tr>
                          )}
                          {parseAmount(documentConfig?.balanceDue) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        BalanceDue
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          fontWeight: "600",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.balanceDue}
                                      </td>
                                    </tr>
                                  )}
                        </tbody>
                      </table>
                    )}             
              {storeSettings?.negobillBranding && (
                <div className="text-center mt-1 font-11">
                  Powered By <strong>NegoBill.com</strong>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchaseView;
